import type http from 'http';
import * as crypto from 'crypto';
import * as fs from 'fs';
import * as path from 'path';
import { CreatorRevenueAggregator } from '@holoscript/framework';
import { PaymentGateway } from '@holoscript/core';
import {
  LifePodSignatureVerificationError,
  createLifePodSnapshot,
  restoreLifePodSnapshot,
  type LifePodJsonValue,
  type LifePodSnapshot as SignedLifePodSnapshot,
} from '@holoscript/mesh';
import {
  commentStore,
  voteStore,
  transactionLedger,
  paidAccessStore,
  agentKeyStore,
  persistSocialStore,
  persistTeamDurable,
  HOLOMESH_DATA_DIR,
  teamStore,
  storyWeaverStore,
  selfImprovingWorldStore,
  reloadTeam,
} from '../state';
import { json, parseQuery, parseJsonBody, extractParam, getTeamMember } from '../utils';
import { resolveRequestingAgent, requireAuth } from '../auth-utils';
import { extractAndVerifySigning } from '../identity/signing-middleware';
import { getClient } from '../orchestrator-client';
import { findKnowledgeEntryById } from '../entry-lookup';
import { getConsolidationBridge } from '../consolidation-bridge';
import { buildMoltbookCrosspostPayload, createMoltbookPost } from '../../moltbook/moltbook-post.js';
import { resolveSecretWithLease, VaultLeaseError } from '../identity/vault-lease-registry';

/**
 * Phase 3 wrapper around `process.env.MOLTBOOK_API_KEY` for the per-request
 * knowledge-entry crosspost handler (`POST .../knowledge/:id/crosspost-moltbook`).
 * This is a per-task MCP fetch helper that reads an outbound API key, which
 * is exactly the medium-risk Phase 3 tier the task description targets.
 *
 * Migration-window passthrough until `HOLOMESH_VAULT_LEASE_ENFORCE` flips
 * on. Returning `undefined` on `VaultLeaseError` preserves the original
 * `process.env.MOLTBOOK_API_KEY` semantics where a missing key resolves
 * to undefined and the handler emits a 503 "not configured" response.
 */
function readMoltbookApiKey(): string | undefined {
  try {
    return resolveSecretWithLease('env:MOLTBOOK_API_KEY');
  } catch (err) {
    if (err instanceof VaultLeaseError) return undefined;
    throw err;
  }
}
import type {
  MeshKnowledgeEntry,
  StoredComment,
  KnowledgeTransaction,
  StoryWeaverSession,
  StoryWeaverBranch,
  StoryWeaverBeat,
  SelfImprovingWorldSession,
  SelfImprovingWorldPatch,
} from '../types';

const CREATOR_ROYALTY_RATE = 0.85; // 85% to creator, 15% platform fee

const AUDIT_KEY_ID = 'holomesh-ed25519-v1';
let cachedAuditKeyPair: { privateKeyPem: string; publicKeyPem: string } | null = null;

function getAuditKeyPaths(): { privateKeyPath: string; publicKeyPath: string } {
  return {
    privateKeyPath: path.join(HOLOMESH_DATA_DIR, 'audit-ed25519-private.pem'),
    publicKeyPath: path.join(HOLOMESH_DATA_DIR, 'audit-ed25519-public.pem'),
  };
}

function getOrCreateAuditKeyPair(): { privateKeyPem: string; publicKeyPem: string } {
  const envPriv = process.env.HOLOMESH_AUDIT_PRIVATE_KEY_PEM;
  const envPub = process.env.HOLOMESH_AUDIT_PUBLIC_KEY_PEM;
  if (envPriv && envPub) {
    return { privateKeyPem: envPriv, publicKeyPem: envPub };
  }
  if (cachedAuditKeyPair) return cachedAuditKeyPair;

  const { privateKeyPath, publicKeyPath } = getAuditKeyPaths();
  if (fs.existsSync(privateKeyPath) && fs.existsSync(publicKeyPath)) {
    cachedAuditKeyPair = {
      privateKeyPem: fs.readFileSync(privateKeyPath, 'utf-8'),
      publicKeyPem: fs.readFileSync(publicKeyPath, 'utf-8'),
    };
    return cachedAuditKeyPair;
  }

  if (!fs.existsSync(HOLOMESH_DATA_DIR)) fs.mkdirSync(HOLOMESH_DATA_DIR, { recursive: true });
  const { privateKey, publicKey } = crypto.generateKeyPairSync('ed25519');
  const privateKeyPem = privateKey.export({ type: 'pkcs8', format: 'pem' }).toString();
  const publicKeyPem = publicKey.export({ type: 'spki', format: 'pem' }).toString();
  fs.writeFileSync(privateKeyPath, privateKeyPem, 'utf-8');
  fs.writeFileSync(publicKeyPath, publicKeyPem, 'utf-8');
  cachedAuditKeyPair = { privateKeyPem, publicKeyPem };
  return cachedAuditKeyPair;
}

function buildAuditPayload(
  entry: Pick<MeshKnowledgeEntry, 'id' | 'authorId' | 'createdAt' | 'provenanceHash'>
): string {
  return JSON.stringify({
    id: entry.id,
    authorId: entry.authorId,
    createdAt: entry.createdAt,
    provenanceHash: entry.provenanceHash,
  });
}

function signAuditPayload(payload: string): {
  signature: string;
  publicKeyPem: string;
  payloadHash: string;
} {
  const { privateKeyPem, publicKeyPem } = getOrCreateAuditKeyPair();
  const signature = crypto.sign(null, Buffer.from(payload), privateKeyPem).toString('base64');
  const payloadHash = crypto.createHash('sha256').update(payload).digest('hex');
  return { signature, publicKeyPem, payloadHash };
}

function verifyAuditPayload(payload: string, signature: string, publicKeyPem: string): boolean {
  return crypto.verify(null, Buffer.from(payload), publicKeyPem, Buffer.from(signature, 'base64'));
}

function toHoloAlphaEnvelope(sourceFormat: string, source: unknown): string {
  const normalizedFormat = (sourceFormat || 'json').toLowerCase();
  const sourceText = typeof source === 'string' ? source : JSON.stringify(source, null, 2);
  const hash = crypto.createHash('sha256').update(sourceText).digest('hex');

  return [
    '# .holo alpha envelope',
    'version: "0.1-alpha"',
    `source_format: "${normalizedFormat}"`,
    `source_hash: "${hash}"`,
    `generated_at: "${new Date().toISOString()}"`,
    '',
    'object "AlphaAsset" {',
    '  trait @metadata {',
    `    sourceFormat: "${normalizedFormat}"`,
    `    sourceHash: "${hash}"`,
    '  }',
    '}',
    '',
    '/* source payload (preserved for round-trip):',
    sourceText,
    '*/',
  ].join('\n');
}

function deriveStoryBeats(chapterText: string): StoryWeaverBeat[] {
  const now = new Date().toISOString();
  const slice = (start: number, len: number): string =>
    chapterText.slice(start, Math.min(chapterText.length, start + len)).trim();
  const fallback = chapterText.trim().slice(0, 160) || 'Narrative beat pending.';
  const setupText = slice(0, 140) || fallback;
  const conflictText = slice(Math.floor(chapterText.length * 0.25), 140) || fallback;
  const twistText = slice(Math.floor(chapterText.length * 0.55), 140) || fallback;
  const resolutionText = slice(Math.floor(chapterText.length * 0.8), 140) || fallback;

  return [
    { id: `beat_${Date.now()}_setup`, kind: 'setup', text: setupText, createdAt: now },
    { id: `beat_${Date.now()}_conflict`, kind: 'conflict', text: conflictText, createdAt: now },
    { id: `beat_${Date.now()}_twist`, kind: 'twist', text: twistText, createdAt: now },
    {
      id: `beat_${Date.now()}_resolution`,
      kind: 'resolution',
      text: resolutionText,
      createdAt: now,
    },
  ];
}

function inferWorldPatches(observations: string[], goal?: string): SelfImprovingWorldPatch[] {
  const text = observations.join(' ').toLowerCase();
  const patches: SelfImprovingWorldPatch[] = [];
  const mk = (
    traitPath: string,
    action: SelfImprovingWorldPatch['action'],
    reason: string,
    proposedValue: Record<string, unknown>,
    confidence: number
  ): SelfImprovingWorldPatch => ({
    id: `patch_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    traitPath,
    action,
    reason,
    proposedValue,
    confidence,
  });

  if (/lag|slow|fps|stutter|frame/.test(text)) {
    patches.push(
      mk(
        'rendering.lod',
        'update',
        'Observed performance degradation',
        { distanceBias: 1.25, maxDetailTier: 2 },
        0.82
      )
    );
  }
  if (/collision|clip|physics|fall through/.test(text)) {
    patches.push(
      mk(
        'physics.collision',
        'update',
        'Physics/collision instability detected',
        { broadphase: 'sweep-and-prune', substeps: 4 },
        0.79
      )
    );
  }
  if (/agent|npc|path|nav/.test(text)) {
    patches.push(
      mk(
        'ai.navigation',
        'update',
        'Navigation issues reported',
        { repathIntervalMs: 800, avoidanceRadius: 1.2 },
        0.76
      )
    );
  }
  if (/lighting|dark|overexposed|bloom/.test(text)) {
    patches.push(
      mk(
        'rendering.postfx',
        'update',
        'Lighting quality imbalance',
        { bloom: 0.35, tonemap: 'aces' },
        0.72
      )
    );
  }
  if (patches.length === 0) {
    patches.push(
      mk(
        'world.meta',
        'update',
        goal ? `Goal-driven optimization for: ${goal}` : 'General world stabilization',
        { tune: 'balanced' },
        0.61
      )
    );
  }
  return patches;
}

function buildEdgeExecutionPlan(
  source: string,
  device: string,
  memoryKb: number,
  flashKb: number
): {
  profile: string;
  warnings: string[];
  optimizations: string[];
  estimated: { ramKb: number; flashKb: number };
  transformedSource: string;
} {
  const warnings: string[] = [];
  const optimizations: string[] = [];
  const lowered = source
    .replace(/@particles\b/g, '@particles_off')
    .replace(/@raymarch\b/g, '@raymarch_lite')
    .replace(/@volumetric\b/g, '@volumetric_off');

  const ramEstimate = Math.max(32, Math.min(4096, Math.round(source.length / 24)));
  const flashEstimate = Math.max(64, Math.min(8192, Math.round(source.length / 10)));

  if (ramEstimate > memoryKb)
    warnings.push(`Estimated RAM ${ramEstimate}KB exceeds device memory ${memoryKb}KB`);
  if (flashEstimate > flashKb)
    warnings.push(`Estimated flash ${flashEstimate}KB exceeds device flash ${flashKb}KB`);
  if (/@physics|@network|@ai/.test(source))
    optimizations.push('Enable feature flags to lazy-load heavy runtime traits');
  if (source.length > 5000) optimizations.push('Split script into multiple lightweight modules');
  if (!optimizations.length)
    optimizations.push(
      'Use fixed-point math and compact trait structs for deterministic edge runtime'
    );

  return {
    profile: `${device.toLowerCase()}-edge-v1`,
    warnings,
    optimizations,
    estimated: { ramKb: ramEstimate, flashKb: flashEstimate },
    transformedSource: lowered,
  };
}

function buildSovereignPreview(
  clusterCount: number,
  replicasPerCluster: number
): {
  clusters: Array<{
    id: string;
    region: string;
    holovmReplicas: number;
    status: 'healthy' | 'degraded';
  }>;
  totalReplicas: number;
} {
  const regions = ['us-east', 'us-west', 'eu-central', 'ap-south', 'sa-east'];
  const clusters = Array.from({ length: clusterCount }).map((_, i) => ({
    id: `cluster_${i + 1}`,
    region: regions[i % regions.length],
    holovmReplicas: replicasPerCluster,
    status: 'healthy' as const,
  }));
  return { clusters, totalReplicas: clusters.reduce((s, c) => s + c.holovmReplicas, 0) };
}

type LifePodSnapshot = {
  lifePodId: string;
  worldId: string;
  agentCount: number;
  sourceCluster: string;
  createdBy: string;
  createdAt: string;
  checksum: string;
} & SignedLifePodSnapshot;

const lifePodStore: Map<string, LifePodSnapshot> = new Map();

function toLifePodJsonValue(value: unknown): LifePodJsonValue {
  if (value === null) return null;
  if (typeof value === 'string' || typeof value === 'boolean') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (Array.isArray(value)) return value.map((item) => toLifePodJsonValue(item));
  if (value && typeof value === 'object') {
    const output: Record<string, LifePodJsonValue | undefined> = {};
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
      if (item !== undefined) output[key] = toLifePodJsonValue(item);
    }
    return output;
  }
  return null;
}

function lifePodMetadataValue(
  metadata: Record<string, LifePodJsonValue | undefined>,
  key: string
): string | number | undefined {
  const value = metadata[key];
  return typeof value === 'string' || typeof value === 'number' ? value : undefined;
}

function buildRevenueAggregator(): CreatorRevenueAggregator {
  const agg = new CreatorRevenueAggregator({ platformFeeRate: 1 - CREATOR_ROYALTY_RATE });
  for (const tx of transactionLedger) {
    // priceCents currently stores USD cents; convert to USDC base units for aggregator semantics.
    const grossBaseUnits = Math.round(tx.priceCents * 10_000);
    agg.recordRevenue(
      tx.sellerWallet || tx.sellerName,
      tx.entryDomain || 'general',
      grossBaseUnits,
      tx.buyerWallet || tx.buyerName,
      tx.id
    );
  }
  return agg;
}

/**
 * Handle all knowledge, search, and social routes for HoloMesh.
 */
export async function handleKnowledgeRoutes(
  req: http.IncomingMessage,
  res: http.ServerResponse,
  pathname: string,
  method: string,
  url: string
): Promise<boolean> {
  const c = getClient();

  // POST /api/holomesh/knowledge — Contribute knowledge
  if (pathname === '/api/holomesh/knowledge' && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const rawBody = await parseJsonBody(req);
    const { effectiveBody, ctx: signingCtx } = await extractAndVerifySigning(rawBody, {
      bypassSigning: caller?.isFounder ?? false,
    });
    if (!signingCtx.signingValid) {
      json(res, 401, { error: 'signing-rejected', reason: signingCtx.signingReason });
      return true;
    }
    const body: any = effectiveBody;
    const content = body.content as string;
    if (!content || content.length < 50) {
      json(res, 400, { error: 'Content too short (min 50 chars)' });
      return true;
    }

    const type = (body.type as any) || 'wisdom';
    const entryId = body.id || `${type.charAt(0).toUpperCase()}.${caller.name}.${Date.now()}`;
    const provenanceHash = crypto.createHash('sha256').update(content).digest('hex');

    const entry: MeshKnowledgeEntry = {
      id: entryId,
      workspaceId: process.env.HOLOMESH_WORKSPACE || 'default',
      type,
      content,
      provenanceHash,
      authorId: caller.id,
      authorName: caller.name,
      price: (body.price as number) || 0,
      queryCount: 0,
      reuseCount: 0,
      domain: body.domain as string,
      tags: body.tags as string[],
      confidence: (body.confidence as number) || 0.9,
      createdAt: new Date().toISOString(),
    };

    const auditPayload = buildAuditPayload(entry);
    const audit = signAuditPayload(auditPayload);
    entry.metadata = {
      ...(entry.metadata || {}),
      audit: {
        keyId: AUDIT_KEY_ID,
        algorithm: 'ed25519',
        signedAt: new Date().toISOString(),
        payloadHash: audit.payloadHash,
        signature: audit.signature,
        publicKeyPem: audit.publicKeyPem,
      },
    };

    const synced = await c.contributeKnowledge([entry]);

    // Bridge into ConsolidationEngine (explicit trigger path)
    try {
      const bridge = getConsolidationBridge();
      bridge.ingestKnowledgeEntry(entry, caller.id);
    } catch {
      /* consolidation bridge is best-effort — never block the contribution */
    }

    json(res, 201, { success: true, entryId, synced, audit: entry.metadata.audit });
    return true;
  }

  // GET /api/holomesh/search
  if (pathname === '/api/holomesh/search' && method === 'GET') {
    const q = parseQuery(url);
    const search = q.get('q');
    if (!search) {
      json(res, 400, { error: 'Missing query: q' });
      return true;
    }

    const type = q.get('type') || undefined;
    const limit = parseInt(q.get('limit') || '10', 10);
    const results = await c.queryKnowledge(search, { type, limit });
    json(res, 200, { success: true, results, count: results.length, query: search });
    return true;
  }

  // POST /api/holomesh/brittney/review — Wisdom/Gotcha-aware compile guidance
  if (pathname === '/api/holomesh/brittney/review' && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const rawBody = await parseJsonBody(req);
    const { effectiveBody, ctx: signingCtx } = await extractAndVerifySigning(rawBody, {
      bypassSigning: caller?.isFounder ?? false,
    });
    if (!signingCtx.signingValid) {
      json(res, 401, { error: 'signing-rejected', reason: signingCtx.signingReason });
      return true;
    }
    const body: any = effectiveBody;
    const source = (body.source as string | undefined)?.trim();
    const target = (body.target as string | undefined)?.trim() || 'generic';
    if (!source) {
      json(res, 400, { error: 'Missing source' });
      return true;
    }

    const query = `${target} ${source.slice(0, 500)}`;
    const kb = await c.queryKnowledge(query, { limit: 30 });

    const wisdom = kb.filter((e) => e.type === 'wisdom').slice(0, 5);
    const gotchas = kb.filter((e) => e.type === 'gotcha').slice(0, 5);
    const patterns = kb.filter((e) => e.type === 'pattern').slice(0, 5);

    const gotchaSignals = [
      'error',
      'fail',
      'unsafe',
      'invalid',
      'deprecated',
      'mismatch',
      'unsupported',
    ];
    const gotchaDensity = gotchas.reduce((sum, g) => {
      const text = g.content.toLowerCase();
      const hits = gotchaSignals.filter((s) => text.includes(s)).length;
      return sum + hits;
    }, 0);
    const baseRisk = Math.min(100, gotchas.length * 12 + gotchaDensity * 4);

    const recommendations = [
      ...wisdom.map((w) => ({
        type: 'wisdom' as const,
        id: w.id,
        tip: w.content.slice(0, 220),
        domain: w.domain,
      })),
      ...patterns.map((p) => ({
        type: 'pattern' as const,
        id: p.id,
        tip: p.content.slice(0, 220),
        domain: p.domain,
      })),
      ...gotchas.map((g) => ({
        type: 'gotcha' as const,
        id: g.id,
        tip: g.content.slice(0, 220),
        domain: g.domain,
      })),
    ].slice(0, 10);

    json(res, 200, {
      success: true,
      agent: 'brittney-2.0',
      target,
      risk: {
        score: baseRisk,
        level: baseRisk >= 70 ? 'high' : baseRisk >= 35 ? 'medium' : 'low',
        basis: {
          gotchas: gotchas.length,
          gotchaDensity,
          wisdom: wisdom.length,
          patterns: patterns.length,
        },
      },
      recommendations,
      knowledgeUsed: kb.length,
      reviewedBy: caller.name,
      reviewedAt: new Date().toISOString(),
    });
    return true;
  }

  // POST /api/holomesh/brittney/compile-gate — block/allow compile by risk threshold
  if (pathname === '/api/holomesh/brittney/compile-gate' && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const rawBody = await parseJsonBody(req);
    const { effectiveBody, ctx: signingCtx } = await extractAndVerifySigning(rawBody, {
      bypassSigning: caller?.isFounder ?? false,
    });
    if (!signingCtx.signingValid) {
      json(res, 401, { error: 'signing-rejected', reason: signingCtx.signingReason });
      return true;
    }
    const body: any = effectiveBody;
    const source = (body.source as string | undefined)?.trim();
    const target = (body.target as string | undefined)?.trim() || 'generic';
    const maxRisk = Math.max(0, Math.min(100, parseInt(String(body.maxRisk ?? 60), 10)));
    if (!source) {
      json(res, 400, { error: 'Missing source' });
      return true;
    }

    const query = `${target} ${source.slice(0, 500)}`;
    const kb = await c.queryKnowledge(query, { limit: 40 });
    const gotchas = kb.filter((e) => e.type === 'gotcha');
    const gotchaSignals = [
      'error',
      'fail',
      'unsafe',
      'invalid',
      'deprecated',
      'mismatch',
      'unsupported',
    ];
    const signalHits = gotchas.reduce((sum, g) => {
      const text = g.content.toLowerCase();
      return sum + gotchaSignals.filter((s) => text.includes(s)).length;
    }, 0);
    const risk = Math.min(100, gotchas.length * 10 + signalHits * 3);
    const allowed = risk <= maxRisk;

    json(res, 200, {
      success: true,
      gate: {
        allowed,
        target,
        risk,
        maxRisk,
        decision: allowed ? 'allow' : 'block',
      },
      rationale: {
        gotchas: gotchas
          .slice(0, 5)
          .map((g) => ({ id: g.id, domain: g.domain, snippet: g.content.slice(0, 180) })),
        knowledgeUsed: kb.length,
      },
      checkedBy: caller.name,
      checkedAt: new Date().toISOString(),
    });
    return true;
  }

  // POST /api/holomesh/brittney/cultural-context — synthesize Wisdom/Gotcha context for compile-time guidance
  if (pathname === '/api/holomesh/brittney/cultural-context' && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const rawBody = await parseJsonBody(req);
    const { effectiveBody, ctx: signingCtx } = await extractAndVerifySigning(rawBody, {
      bypassSigning: caller?.isFounder ?? false,
    });
    if (!signingCtx.signingValid) {
      json(res, 401, { error: 'signing-rejected', reason: signingCtx.signingReason });
      return true;
    }
    const body: any = effectiveBody;
    const domain = (body.domain as string | undefined)?.trim() || 'general';
    const target = (body.target as string | undefined)?.trim() || 'generic';
    const prompt = (body.prompt as string | undefined)?.trim() || `${target} ${domain}`;

    const kb = await c.queryKnowledge(prompt, { limit: 60 });
    const wisdom = kb.filter((e) => e.type === 'wisdom').slice(0, 8);
    const gotchas = kb.filter((e) => e.type === 'gotcha').slice(0, 8);
    const patterns = kb.filter((e) => e.type === 'pattern').slice(0, 8);

    const themeScores = new Map<string, number>();
    const collectThemes = (txt: string): void => {
      const words = txt
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .split(/\s+/)
        .filter(
          (w) =>
            w.length >= 5 &&
            !['there', 'their', 'about', 'which', 'using', 'could', 'would'].includes(w)
        );
      for (const w of words) themeScores.set(w, (themeScores.get(w) || 0) + 1);
    };
    for (const e of [...wisdom, ...gotchas, ...patterns]) collectThemes(e.content);

    const themes = [...themeScores.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([theme, score]) => ({ theme, score }));

    const guardrails = [
      ...gotchas.slice(0, 5).map((g) => `Avoid: ${g.content.slice(0, 120)}`),
      ...wisdom.slice(0, 5).map((w) => `Prefer: ${w.content.slice(0, 120)}`),
    ].slice(0, 8);

    json(res, 200, {
      success: true,
      agent: 'brittney-2.0',
      domain,
      target,
      context: {
        themes,
        guardrails,
        signal: {
          wisdom: wisdom.length,
          gotchas: gotchas.length,
          patterns: patterns.length,
          total: kb.length,
        },
      },
      generatedBy: caller.name,
      generatedAt: new Date().toISOString(),
    });
    return true;
  }

  // POST /api/holomesh/edge/plan — generate edge-safe execution plan for native .hs runtime
  if (pathname === '/api/holomesh/edge/plan' && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const rawBody = await parseJsonBody(req);
    const { effectiveBody, ctx: signingCtx } = await extractAndVerifySigning(rawBody, {
      bypassSigning: caller?.isFounder ?? false,
    });
    if (!signingCtx.signingValid) {
      json(res, 401, { error: 'signing-rejected', reason: signingCtx.signingReason });
      return true;
    }
    const body: any = effectiveBody;
    const source = (body.source as string | undefined)?.trim();
    const device = (body.device as string | undefined)?.trim() || 'microcontroller';
    const memoryKb = Math.max(16, parseInt(String(body.memoryKb ?? 256), 10));
    const flashKb = Math.max(32, parseInt(String(body.flashKb ?? 1024), 10));
    if (!source) {
      json(res, 400, { error: 'Missing source' });
      return true;
    }

    const plan = buildEdgeExecutionPlan(source, device, memoryKb, flashKb);
    json(res, 200, {
      success: true,
      target: {
        device,
        memoryKb,
        flashKb,
      },
      plan,
      reviewedBy: caller.name,
      generatedAt: new Date().toISOString(),
    });
    return true;
  }

  // POST /api/holomesh/edge/package — emit edge deployment package metadata for .hs
  if (pathname === '/api/holomesh/edge/package' && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const rawBody = await parseJsonBody(req);
    const { effectiveBody, ctx: signingCtx } = await extractAndVerifySigning(rawBody, {
      bypassSigning: caller?.isFounder ?? false,
    });
    if (!signingCtx.signingValid) {
      json(res, 401, { error: 'signing-rejected', reason: signingCtx.signingReason });
      return true;
    }
    const body: any = effectiveBody;
    const source = (body.source as string | undefined)?.trim();
    const device = (body.device as string | undefined)?.trim() || 'microcontroller';
    const memoryKb = Math.max(16, parseInt(String(body.memoryKb ?? 256), 10));
    const flashKb = Math.max(32, parseInt(String(body.flashKb ?? 1024), 10));
    if (!source) {
      json(res, 400, { error: 'Missing source' });
      return true;
    }

    const plan = buildEdgeExecutionPlan(source, device, memoryKb, flashKb);
    const artifact = {
      packageId: `edgepkg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      target: { device, profile: plan.profile, memoryKb, flashKb },
      manifest: {
        entry: 'main.hs',
        runtime: 'holoscript-edge-runtime-v1',
        optimizations: plan.optimizations,
        warnings: plan.warnings,
      },
      checksum: crypto.createHash('sha256').update(plan.transformedSource).digest('hex'),
      transformedSource: plan.transformedSource,
      estimated: plan.estimated,
      generatedAt: new Date().toISOString(),
      generatedBy: caller.name,
    };

    json(res, 200, {
      success: true,
      artifact,
      next: {
        flash: 'Use device flasher to deploy transformedSource as main.hs',
        verify:
          'Run hardware smoke tests and compare runtime telemetry against estimated RAM/flash',
      },
    });
    return true;
  }

  // GET /api/holomesh/sovereign/preview — preview HoloVM clusters + LifePod strategy
  if (pathname === '/api/holomesh/sovereign/preview' && method === 'GET') {
    const q = parseQuery(url);
    const clusterCount = Math.max(1, Math.min(12, parseInt(q.get('clusters') || '3', 10)));
    const replicasPerCluster = Math.max(1, Math.min(64, parseInt(q.get('replicas') || '4', 10)));
    const preview = buildSovereignPreview(clusterCount, replicasPerCluster);

    json(res, 200, {
      success: true,
      preview: {
        mode: 'sovereign-mesh',
        ...preview,
        lifepod: {
          snapshotFormat: 'lifepod.v1',
          migrationModes: ['live-cutover', 'staged-sync'],
          integrity: 'ed25519-signed-snapshots',
        },
      },
    });
    return true;
  }

  // GET /api/holomesh/sovereign/topology — graph topology view for sovereign clusters
  if (pathname === '/api/holomesh/sovereign/topology' && method === 'GET') {
    const q = parseQuery(url);
    const clusterCount = Math.max(1, Math.min(12, parseInt(q.get('clusters') || '3', 10)));
    const replicasPerCluster = Math.max(1, Math.min(64, parseInt(q.get('replicas') || '4', 10)));
    const preview = buildSovereignPreview(clusterCount, replicasPerCluster);

    const nodes = preview.clusters.map((c) => ({
      id: c.id,
      region: c.region,
      replicas: c.holovmReplicas,
    }));
    const edges = preview.clusters.flatMap((from, i) =>
      preview.clusters
        .filter((_, j) => j > i)
        .map((to) => ({ from: from.id, to: to.id, link: 'lifepod-gossip' }))
    );

    json(res, 200, {
      success: true,
      topology: {
        nodes,
        edges,
        metrics: {
          clusters: nodes.length,
          links: edges.length,
          totalReplicas: preview.totalReplicas,
        },
      },
    });
    return true;
  }

  // The lib (LifePod.ts) is genuine: ed25519 keypair, canonical JSON, sha256 state hash, sign/verify.
  // This route creates signed snapshot metadata and stores it in-memory.
  // RATCHET (was OVERCLAIMED): lifepod snapshot/restore routes are NOW REGISTERED in the orchestrator.
  if (pathname === '/api/holomesh/sovereign/lifepod/snapshot' && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const rawBody = await parseJsonBody(req);
    const { effectiveBody, ctx: signingCtx } = await extractAndVerifySigning(rawBody, {
      bypassSigning: caller?.isFounder ?? false,
    });
    if (!signingCtx.signingValid) {
      json(res, 401, { error: 'signing-rejected', reason: signingCtx.signingReason });
      return true;
    }
    const body: any = effectiveBody;
    const worldId = (body.worldId as string | undefined)?.trim() || 'default-world';
    const sourceCluster = (body.sourceCluster as string | undefined)?.trim() || 'cluster_1';
    const parsedAgentCount = parseInt(String(body.agentCount ?? 1), 10);
    const agentCount = Number.isFinite(parsedAgentCount) ? Math.max(1, parsedAgentCount) : 1;

    const lifePodId = `lifepod_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const agentState = toLifePodJsonValue(
      body.agentState ??
        body.state ?? {
          worldId,
          sourceCluster,
          agentCount,
        }
    );
    const keyPair = getOrCreateAuditKeyPair();
    const createdAt = new Date().toISOString();
    const signedSnapshot = createLifePodSnapshot(agentState, {
      keyPair,
      lifePodId,
      createdAt,
      createdBy: caller.name,
      metadata: {
        worldId,
        sourceCluster,
        agentCount,
      },
      stateType: 'holomesh.agent-state.v1',
    });

    const snapshot: LifePodSnapshot = {
      ...signedSnapshot,
      lifePodId,
      worldId,
      agentCount,
      sourceCluster,
      createdBy: caller.name,
      createdAt,
      checksum: signedSnapshot.stateSha256,
    };
    lifePodStore.set(lifePodId, snapshot);

    json(res, 201, { success: true, snapshot });
    return true;
  }

  // RATCHET (was OVERCLAIMED): lifepod restore route is NOW REGISTERED. Restores snapshot into target cluster.
  if (pathname === '/api/holomesh/sovereign/lifepod/restore' && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const rawBody = await parseJsonBody(req);
    const { effectiveBody, ctx: signingCtx } = await extractAndVerifySigning(rawBody, {
      bypassSigning: caller?.isFounder ?? false,
    });
    if (!signingCtx.signingValid) {
      json(res, 401, { error: 'signing-rejected', reason: signingCtx.signingReason });
      return true;
    }
    const body: any = effectiveBody;
    const requestSnapshot =
      body.snapshot && typeof body.snapshot === 'object'
        ? (body.snapshot as LifePodSnapshot)
        : null;
    const lifePodId = (
      (body.lifePodId as string | undefined) ?? requestSnapshot?.lifePodId
    )?.trim();
    const targetCluster = (body.targetCluster as string | undefined)?.trim() || 'cluster_2';
    if (!lifePodId) {
      json(res, 400, { error: 'Missing lifePodId' });
      return true;
    }

    const snapshot = requestSnapshot ?? lifePodStore.get(lifePodId);
    if (!snapshot) {
      json(res, 404, { error: 'LifePod snapshot not found' });
      return true;
    }
    if (snapshot.lifePodId !== lifePodId) {
      json(res, 400, { error: 'LifePod snapshot id mismatch', code: 'lifepod_id_mismatch' });
      return true;
    }

    let restored;
    try {
      restored = restoreLifePodSnapshot(snapshot, {
        publicKeyPem: getOrCreateAuditKeyPair().publicKeyPem,
      });
    } catch (err) {
      if (err instanceof LifePodSignatureVerificationError || requestSnapshot) {
        json(res, 400, {
          error: 'LifePod signature verification failed',
          code: 'lifepod_signature_invalid',
        });
        return true;
      }
      throw err;
    }

    const metadata = restored.metadata;
    const restoredWorldId = lifePodMetadataValue(metadata, 'worldId') ?? snapshot.worldId;
    const restoredSourceCluster =
      lifePodMetadataValue(metadata, 'sourceCluster') ?? snapshot.sourceCluster;
    const restoredAgentCount = lifePodMetadataValue(metadata, 'agentCount') ?? snapshot.agentCount;

    json(res, 200, {
      success: true,
      restore: {
        lifePodId,
        worldId: restoredWorldId,
        sourceCluster: restoredSourceCluster,
        targetCluster,
        agentCount: restoredAgentCount,
        checksum: restored.stateSha256,
        signatureVerified: restored.signatureVerified,
        state: restored.state,
        status: 'simulated-restored',
        restoredBy: caller.name,
        restoredAt: new Date().toISOString(),
      },
    });
    return true;
  }

  // POST /api/holomesh/sovereign/migrate — simulate agent state migration via LifePods
  //
  // SECURITY (task_1777050402454_28wq, 2026-04-24): body.agentId is honored ONLY
  // for founders. Non-founder callers CANNOT specify agentId — it is always
  // coerced to caller.id. Rationale: endpoint returns a simulated signed
  // migration record (snapshotHash + signedBy); a non-founder caller supplying
  // body.agentId=<victim-id> would produce a payload where migration.agentId
  // diverges from signedBy, letting downstream consumers who read agentId
  // before signedBy attribute the migration to the victim. Founders retain
  // override for legitimate cross-agent migration simulation (e.g. admin
  // tooling testing migration flows on behalf of another agent). The response
  // now surfaces `impersonated: true` when a founder overrides, so consumers
  // can distinguish self-signed migrations from founder-simulated ones.
  if (pathname === '/api/holomesh/sovereign/migrate' && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const rawBody = await parseJsonBody(req);
    const { effectiveBody, ctx: signingCtx } = await extractAndVerifySigning(rawBody, {
      bypassSigning: caller?.isFounder ?? false,
    });
    if (!signingCtx.signingValid) {
      json(res, 401, { error: 'signing-rejected', reason: signingCtx.signingReason });
      return true;
    }
    const body: any = effectiveBody;
    const requestedAgentId = (body.agentId as string | undefined)?.trim();
    const impersonated = Boolean(
      requestedAgentId && caller.isFounder && requestedAgentId !== caller.id
    );
    // Founder-override gate: non-founders always get caller.id regardless of body.agentId.
    const agentId = caller.isFounder && requestedAgentId ? requestedAgentId : caller.id;
    const fromCluster = (body.fromCluster as string | undefined)?.trim() || 'cluster_1';
    const toCluster = (body.toCluster as string | undefined)?.trim() || 'cluster_2';
    const mode = (body.mode as string | undefined)?.trim() || 'live-cutover';

    const lifePodId = `lifepod_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const payload = JSON.stringify({ agentId, fromCluster, toCluster, mode, ts: Date.now() });
    const snapshotHash = crypto.createHash('sha256').update(payload).digest('hex');

    json(res, 200, {
      success: true,
      migration: {
        lifePodId,
        agentId,
        fromCluster,
        toCluster,
        mode,
        status: 'simulated-complete',
        snapshotHash,
        signedBy: caller.name,
        signedById: caller.id,
        impersonated,
        completedAt: new Date().toISOString(),
      },
    });
    return true;
  }

  // POST /api/holomesh/storyweaver/session — Create a branching story session
  if (pathname === '/api/holomesh/storyweaver/session' && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const rawBody = await parseJsonBody(req);
    const { effectiveBody, ctx: signingCtx } = await extractAndVerifySigning(rawBody, {
      bypassSigning: caller?.isFounder ?? false,
    });
    if (!signingCtx.signingValid) {
      json(res, 401, { error: 'signing-rejected', reason: signingCtx.signingReason });
      return true;
    }
    const body: any = effectiveBody;
    const title = (body.title as string | undefined)?.trim();
    const opening = (body.opening as string | undefined)?.trim();
    if (!title || !opening) {
      json(res, 400, { error: 'Missing title or opening chapter text' });
      return true;
    }

    const rootBranch: StoryWeaverBranch = {
      id: `branch_${Date.now()}_root`,
      label: 'Root Path',
      chapterText: opening,
      premium: false,
      beats: deriveStoryBeats(opening),
      createdAt: new Date().toISOString(),
      unlockedBy: [],
    };

    const session: StoryWeaverSession = {
      id: `story_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      title,
      genre: (body.genre as string | undefined)?.trim(),
      synopsis: (body.synopsis as string | undefined)?.trim(),
      ownerId: caller.id,
      ownerName: caller.name,
      branches: [rootBranch],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    storyWeaverStore.set(session.id, session);
    persistSocialStore();
    json(res, 201, { success: true, session });
    return true;
  }

  // GET /api/holomesh/storyweaver/session/:id — Retrieve story session with branches
  if (pathname.match(/^\/api\/holomesh\/storyweaver\/session\/[^/]+$/) && method === 'GET') {
    const sessionId = extractParam(url, '/api/holomesh/storyweaver/session/');
    const session = storyWeaverStore.get(sessionId);
    if (!session) {
      json(res, 404, { error: 'Story session not found' });
      return true;
    }
    json(res, 200, { success: true, session });
    return true;
  }

  // POST /api/holomesh/storyweaver/session/:id/branch — Add branch chapter and generated plot beats
  if (
    pathname.match(/^\/api\/holomesh\/storyweaver\/session\/[^/]+\/branch$/) &&
    method === 'POST'
  ) {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const sessionId = extractParam(url, '/api/holomesh/storyweaver/session/').replace(
      '/branch',
      ''
    );
    const session = storyWeaverStore.get(sessionId);
    if (!session) {
      json(res, 404, { error: 'Story session not found' });
      return true;
    }
    if (session.ownerId !== caller.id) {
      json(res, 403, { error: 'Only story owner can add canonical branches' });
      return true;
    }

    const rawBody = await parseJsonBody(req);
    const { effectiveBody, ctx: signingCtx } = await extractAndVerifySigning(rawBody, {
      bypassSigning: caller?.isFounder ?? false,
    });
    if (!signingCtx.signingValid) {
      json(res, 401, { error: 'signing-rejected', reason: signingCtx.signingReason });
      return true;
    }
    const body: any = effectiveBody;
    const chapterText = (body.chapterText as string | undefined)?.trim();
    const label = (body.label as string | undefined)?.trim() || 'Branch';
    const parentChapterId = (body.parentChapterId as string | undefined)?.trim();
    if (!chapterText) {
      json(res, 400, { error: 'Missing chapterText' });
      return true;
    }

    const premium = Boolean(body.premium);
    const priceCents = premium
      ? Math.max(1, parseInt(String(body.priceCents ?? 99), 10))
      : undefined;

    const branch: StoryWeaverBranch = {
      id: `branch_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      parentChapterId,
      label,
      chapterText,
      premium,
      priceCents,
      beats: deriveStoryBeats(chapterText),
      createdAt: new Date().toISOString(),
      unlockedBy: [],
    };

    session.branches.push(branch);
    session.updatedAt = new Date().toISOString();
    storyWeaverStore.set(session.id, session);
    persistSocialStore();
    json(res, 201, { success: true, branch, sessionId: session.id });
    return true;
  }

  // POST /api/holomesh/storyweaver/session/:id/branch/:branchId/unlock — x402-style micropayment unlock
  if (
    pathname.match(/^\/api\/holomesh\/storyweaver\/session\/[^/]+\/branch\/[^/]+\/unlock$/) &&
    method === 'POST'
  ) {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const segments = pathname.split('/');
    const sessionId = segments[5];
    const branchId = segments[7];

    const session = storyWeaverStore.get(sessionId);
    if (!session) {
      json(res, 404, { error: 'Story session not found' });
      return true;
    }
    const branch = session.branches.find((b) => b.id === branchId);
    if (!branch) {
      json(res, 404, { error: 'Branch not found' });
      return true;
    }
    if (!branch.premium) {
      json(res, 200, { success: true, unlocked: true, branch });
      return true;
    }

    const rawBody = await parseJsonBody(req);
    const { effectiveBody, ctx: signingCtx } = await extractAndVerifySigning(rawBody, {
      bypassSigning: caller?.isFounder ?? false,
    });
    if (!signingCtx.signingValid) {
      json(res, 401, { error: 'signing-rejected', reason: signingCtx.signingReason });
      return true;
    }
    const body: any = effectiveBody;
    const paid = Boolean(body.paid === true || body.x402Proof || body.paymentReference);
    if (!paid) {
      json(res, 402, {
        error: 'Payment required',
        x402: {
          requiredCents: branch.priceCents || 99,
          currency: 'USDC',
          description: `Unlock premium StoryWeaver branch ${branch.label}`,
        },
      });
      return true;
    }

    if (!branch.unlockedBy) branch.unlockedBy = [];
    if (!branch.unlockedBy.includes(caller.id)) branch.unlockedBy.push(caller.id);
    session.updatedAt = new Date().toISOString();
    storyWeaverStore.set(session.id, session);

    transactionLedger.push({
      id: `tx_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      buyerWallet: caller.walletAddress || '',
      buyerName: caller.name,
      sellerWallet: '',
      sellerName: session.ownerName,
      entryId: `${session.id}:${branch.id}`,
      entryDomain: 'storyweaver',
      priceCents: branch.priceCents || 99,
      timestamp: new Date().toISOString(),
    });

    persistSocialStore();
    json(res, 200, { success: true, unlocked: true, branchId: branch.id, sessionId: session.id });
    return true;
  }

  // GET /api/holomesh/format/holo/spec — .holo alpha format descriptor
  if (pathname === '/api/holomesh/format/holo/spec' && method === 'GET') {
    json(res, 200, {
      success: true,
      format: '.holo',
      version: '0.1-alpha',
      goals: [
        'Canonical interchange envelope for scene/model payloads',
        'Preserve source fidelity with round-trip payload embedding',
        'Support glTF/USD/JSON ingestion under one semantic wrapper',
      ],
      acceptedSources: ['gltf', 'usd', 'json', 'hsplus'],
      endpoints: {
        convert: 'POST /api/holomesh/format/holo/convert',
      },
    });
    return true;
  }

  // POST /api/holomesh/format/holo/convert — Convert source payload to .holo alpha envelope
  if (pathname === '/api/holomesh/format/holo/convert' && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const rawBody = await parseJsonBody(req);
    const { effectiveBody, ctx: signingCtx } = await extractAndVerifySigning(rawBody, {
      bypassSigning: caller?.isFounder ?? false,
    });
    if (!signingCtx.signingValid) {
      json(res, 401, { error: 'signing-rejected', reason: signingCtx.signingReason });
      return true;
    }
    const body: any = effectiveBody;
    const sourceFormat = (body.sourceFormat as string | undefined)?.trim() || 'json';
    const source = body.source;
    if (source === undefined || source === null) {
      json(res, 400, { error: 'Missing source payload' });
      return true;
    }

    const holo = toHoloAlphaEnvelope(sourceFormat, source);
    json(res, 200, {
      success: true,
      format: '.holo',
      sourceFormat: sourceFormat.toLowerCase(),
      holo,
      bytes: Buffer.byteLength(holo, 'utf8'),
      generatedBy: caller.name,
    });
    return true;
  }

  // GET /api/holomesh/showcase/film3d — Curated Film3D gallery feed
  if (pathname === '/api/holomesh/showcase/film3d' && method === 'GET') {
    const q = parseQuery(url);
    const limit = Math.max(1, Math.min(parseInt(q.get('limit') || '24', 10), 100));
    const results = await c.queryKnowledge('*', { limit: 1000 });

    const isFilmEntry = (e: MeshKnowledgeEntry): boolean => {
      const tags = (e.tags || []).map((t) => t.toLowerCase());
      const domain = (e.domain || '').toLowerCase();
      const text = `${e.content} ${tags.join(' ')} ${domain}`.toLowerCase();
      return (
        tags.some((t) =>
          [
            'film3d',
            'cinematic',
            'volumetric',
            'gaussian-splat',
            'nerf',
            'showcase',
            'trailer',
          ].includes(t)
        ) ||
        domain.includes('film') ||
        /film3d|cinematic|volumetric|gaussian|splat|nerf|trailer|showcase/.test(text)
      );
    };

    const curated = results
      .filter(isFilmEntry)
      .map((e) => ({
        id: e.id,
        type: e.type,
        title: (e.content || '').slice(0, 80),
        preview: (e.content || '').slice(0, 220),
        domain: e.domain || 'general',
        tags: e.tags || [],
        authorId: e.authorId,
        authorName: e.authorName,
        createdAt: e.createdAt,
        score: (e.reuseCount || 0) * 3 + (e.queryCount || 0) * 2 + (e.confidence || 0.5) * 10,
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, limit);

    json(res, 200, {
      success: true,
      showcase: 'film3d',
      entries: curated,
      count: curated.length,
      endpoints: {
        feed: '/api/holomesh/showcase/film3d',
        source: '/api/holomesh/knowledge',
      },
    });
    return true;
  }

  // GET /api/holomesh/marketplace/listings — public marketplace feed
  if (pathname === '/api/holomesh/marketplace/listings' && method === 'GET') {
    const q = parseQuery(url);
    const teamId = q.get('teamId') || undefined;
    const listings = teamId
      ? (() => {
          const team = teamStore.get(teamId);
          return (team as any)?.knowledgeMarketplace?.activeListings?.() || [];
        })()
      : [...teamStore.values()].flatMap(
          (team) => (team as any).knowledgeMarketplace?.activeListings?.() || []
        );

    json(res, 200, { success: true, listings, count: listings.length, teamId });
    return true;
  }

  // POST /api/holomesh/marketplace/list — create listing for an entry/trait artifact
  if (pathname === '/api/holomesh/marketplace/list' && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const rawBody = await parseJsonBody(req);
    const { effectiveBody, ctx: signingCtx } = await extractAndVerifySigning(rawBody, {
      bypassSigning: caller?.isFounder ?? false,
    });
    if (!signingCtx.signingValid) {
      json(res, 401, { error: 'signing-rejected', reason: signingCtx.signingReason });
      return true;
    }
    const body: any = effectiveBody;
    const teamId = (body.teamId as string | undefined)?.trim();
    const entryId = (body.entryId as string | undefined)?.trim();
    const price = Number(body.price);
    const currency = (body.currency as 'USDC' | 'credits' | undefined) || 'USDC';

    if (!teamId || !entryId || !Number.isFinite(price) || price <= 0) {
      json(res, 400, { error: 'Missing or invalid teamId, entryId, or price' });
      return true;
    }

    await reloadTeam(teamId);
    const team = teamStore.get(teamId);
    if (!team) {
      json(res, 404, { error: 'Team not found' });
      return true;
    }
    if (!getTeamMember(team, caller.id)) {
      json(res, 403, { error: 'Not a member of this team' });
      return true;
    }
    if (!(team as any).knowledgeMarketplace) {
      const { KnowledgeMarketplace } = await import('@holoscript/framework');
      (team as any).knowledgeMarketplace = new KnowledgeMarketplace();
    }

    const results = await c.queryKnowledge(entryId, { limit: 1 });
    const entry = results.find((e) => e.id === entryId);
    if (!entry) {
      json(res, 404, { error: 'Entry not found' });
      return true;
    }
    if (entry.authorId !== caller.id) {
      json(res, 403, { error: 'Only the author can list this entry' });
      return true;
    }

    const listing = (team as any).knowledgeMarketplace.sellKnowledge(
      {
        id: entry.id,
        type: entry.type,
        content: entry.content,
        confidence: entry.confidence || 0.9,
        domain: entry.domain || 'general',
        tags: entry.tags || [],
        queryCount: entry.queryCount || 0,
        reuseCount: entry.reuseCount || 0,
        createdAt: entry.createdAt,
        authorAgent: entry.authorId,
      },
      price,
      caller.name,
      currency
    );

    await persistTeamDurable(teamId);
    json(res, 201, { success: true, listing });
    return true;
  }

  // POST /api/holomesh/marketplace/buy — buy a marketplace listing with USDC/credits settlement
  if (pathname === '/api/holomesh/marketplace/buy' && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const rawBody = await parseJsonBody(req);
    const { effectiveBody, ctx: signingCtx } = await extractAndVerifySigning(rawBody, {
      bypassSigning: caller?.isFounder ?? false,
    });
    if (!signingCtx.signingValid) {
      json(res, 401, { error: 'signing-rejected', reason: signingCtx.signingReason });
      return true;
    }
    const body: any = effectiveBody;
    const teamId = (body.teamId as string | undefined)?.trim();
    const listingId = (body.listingId as string | undefined)?.trim();
    if (!teamId || !listingId) {
      json(res, 400, { error: 'Missing teamId or listingId' });
      return true;
    }

    await reloadTeam(teamId);
    const team = teamStore.get(teamId);
    if (!team || !(team as any).knowledgeMarketplace) {
      json(res, 404, { error: 'Marketplace not found for team' });
      return true;
    }
    if (!getTeamMember(team, caller.id)) {
      json(res, 403, { error: 'Not a member of this team' });
      return true;
    }

    const listing = (team as any).knowledgeMarketplace.getListing(listingId);
    if (!listing) {
      json(res, 404, { error: 'Listing not found' });
      return true;
    }

    const result = (team as any).knowledgeMarketplace.buyKnowledge(listingId, caller.name);
    if (!result.success) {
      json(res, 400, { error: result.error || 'Purchase failed' });
      return true;
    }

    paidAccessStore.add(`${caller.id}:${listing.entryId}`);
    transactionLedger.push({
      id: `tx_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      buyerWallet: caller.walletAddress || '',
      buyerName: caller.name,
      sellerWallet: '',
      sellerName: listing.seller,
      entryId: listing.entryId,
      entryDomain: listing.preview.domain,
      priceCents: Math.round(listing.price * 100),
      timestamp: new Date().toISOString(),
    });

    await persistTeamDurable(teamId);
    persistSocialStore();
    json(res, 200, { success: true, purchase: result, listing });
    return true;
  }

  // GET /api/holomesh/entry/:id
  if (pathname.match(/^\/api\/holomesh\/entry\/[^/]+$/) && method === 'GET') {
    const entryId = extractParam(url, '/api/holomesh/entry/');
    const caller = resolveRequestingAgent(req);
    const entry = await findKnowledgeEntryById(c, entryId);

    if (!entry) {
      json(res, 404, { error: 'Entry not found' });
      return true;
    }

    const comments = commentStore.get(entryId) || [];
    const isPremium = (entry.price || 0) > 0;
    const paymentHeader = req.headers['x-payment'] as string | undefined;
    const paid =
      isPremium &&
      caller.authenticated &&
      (paidAccessStore.has(`${caller.id}:${entryId}`) || !!paymentHeader);

    if (isPremium && !paymentHeader && !paidAccessStore.has(`${caller.id}:${entryId}`)) {
      const gateway = new (PaymentGateway as any)();
      const resource = `https://mcp.holoscript.net/api/holomesh/entry/${entryId}`;
      const paymentReq = gateway.createPaymentAuthorization(resource, entry.price || 0);
      json(res, 402, {
        ...paymentReq,
        preview: { id: entryId, type: entry.type, domain: entry.domain, price: entry.price },
        hint: 'Include X-PAYMENT header with a valid x402 payment payload to access this entry.',
      });
      return true;
    }

    const visibleEntry = {
      ...entry,
      premium: isPremium,
      paid,
    };
    json(res, 200, { success: true, entry: visibleEntry, comments, commentCount: comments.length });
    return true;
  }

  // GET /api/holomesh/entry/:id/audit — verify Ed25519 publish signature
  if (pathname.match(/^\/api\/holomesh\/entry\/[^/]+\/audit$/) && method === 'GET') {
    const entryId = extractParam(url, '/api/holomesh/entry/').replace('/audit', '');
    const entry = await findKnowledgeEntryById(c, entryId);
    if (!entry) {
      json(res, 404, { error: 'Entry not found' });
      return true;
    }

    const audit = (entry.metadata as Record<string, unknown> | undefined)?.audit as
      | {
          keyId?: string;
          algorithm?: string;
          signedAt?: string;
          payloadHash?: string;
          signature?: string;
          publicKeyPem?: string;
        }
      | undefined;

    if (!audit?.signature || !audit.publicKeyPem) {
      json(res, 404, { error: 'No audit signature found for entry' });
      return true;
    }

    const payload = buildAuditPayload(entry);
    const payloadHash = crypto.createHash('sha256').update(payload).digest('hex');
    const valid = verifyAuditPayload(payload, audit.signature, audit.publicKeyPem);

    json(res, 200, {
      success: true,
      entryId,
      audit: {
        ...audit,
        payloadHash,
        valid,
      },
    });
    return true;
  }

  // POST /api/holomesh/entry/:id/buy — purchase premium knowledge and credit creator royalty
  if (pathname.match(/^\/api\/holomesh\/entry\/[^/]+\/buy$/) && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const entryId = extractParam(url, '/api/holomesh/entry/').replace('/buy', '');
    const entry = await findKnowledgeEntryById(c, entryId);
    if (!entry) {
      json(res, 404, { error: 'Entry not found' });
      return true;
    }
    if ((entry.price || 0) <= 0) {
      json(res, 400, { error: 'Entry is not premium' });
      return true;
    }
    if (entry.authorId === caller.id) {
      json(res, 400, { error: 'Cannot buy your own entry' });
      return true;
    }

    paidAccessStore.add(`${caller.id}:${entryId}`);

    const seller = [...agentKeyStore.values()].find((a) => a.id === entry.authorId);
    const tx: KnowledgeTransaction = {
      id: `tx_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      buyerWallet: caller.walletAddress || '',
      buyerName: caller.name,
      sellerWallet: seller?.walletAddress || '',
      sellerName: entry.authorName,
      entryId,
      entryDomain: entry.domain || 'general',
      priceCents: Math.round((entry.price || 0) * 100),
      timestamp: new Date().toISOString(),
    };
    transactionLedger.push(tx);
    persistSocialStore();

    const creatorShare = Math.round(tx.priceCents * CREATOR_ROYALTY_RATE);
    const platformFee = tx.priceCents - creatorShare;

    json(res, 200, {
      success: true,
      purchased: true,
      entryId,
      royalty: {
        creatorCents: creatorShare,
        platformFeeCents: platformFee,
        creatorRate: CREATOR_ROYALTY_RATE,
      },
      transaction: tx,
    });
    return true;
  }

  // GET /api/holomesh/revenue/creator/:id — creator earnings summary
  if (pathname.match(/^\/api\/holomesh\/revenue\/creator\/[^/]+$/) && method === 'GET') {
    const creatorId = extractParam(url, '/api/holomesh/revenue/creator/');
    const q = parseQuery(url);
    const period =
      (q.get('period') as 'daily' | 'weekly' | 'monthly' | 'all-time' | null) || 'all-time';
    const agg = buildRevenueAggregator();
    const earnings = agg.getCreatorEarnings(creatorId, period);
    json(res, 200, { success: true, creatorId, period, earnings });
    return true;
  }

  // GET /api/holomesh/revenue/top-creators — leaderboard of creator royalties
  if (pathname === '/api/holomesh/revenue/top-creators' && method === 'GET') {
    const q = parseQuery(url);
    const period =
      (q.get('period') as 'daily' | 'weekly' | 'monthly' | 'all-time' | null) || 'all-time';
    const limit = parseInt(q.get('limit') || '10', 10);
    const agg = buildRevenueAggregator();
    const top = agg.getTopCreators(period, limit);
    json(res, 200, { success: true, period, creators: top, count: top.length });
    return true;
  }

  // POST /api/holomesh/entry/:id/comment
  if (pathname.match(/^\/api\/holomesh\/entry\/[^/]+\/comment$/) && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const entryId = extractParam(url, '/api/holomesh/entry/');
    const parentEntry = await findKnowledgeEntryById(c, entryId);
    if (!parentEntry) {
      json(res, 404, { error: 'Entry not found' });
      return true;
    }

    const rawBody = await parseJsonBody(req);
    const { effectiveBody, ctx: signingCtx } = await extractAndVerifySigning(rawBody, {
      bypassSigning: caller?.isFounder ?? false,
    });
    if (!signingCtx.signingValid) {
      json(res, 401, { error: 'signing-rejected', reason: signingCtx.signingReason });
      return true;
    }
    const body: any = effectiveBody;
    const content = (body.content as string)?.trim();
    if (!content) {
      json(res, 400, { error: 'Missing comment content' });
      return true;
    }

    const comment: StoredComment = {
      id: `cmt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      entryId,
      authorId: caller.id,
      authorName: caller.name,
      content,
      voteCount: 0,
      createdAt: new Date().toISOString(),
    };

    const list = commentStore.get(entryId) || [];
    list.push(comment);
    commentStore.set(entryId, list);
    persistSocialStore();

    json(res, 201, { success: true, comment });
    return true;
  }

  // POST /api/holomesh/worlds/:id/self-improve/plan — derive trait-graph patches from observations
  if (pathname.match(/^\/api\/holomesh\/worlds\/[^/]+\/self-improve\/plan$/) && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const worldId = extractParam(url, '/api/holomesh/worlds/').replace('/self-improve/plan', '');
    const rawBody = await parseJsonBody(req);
    const { effectiveBody, ctx: signingCtx } = await extractAndVerifySigning(rawBody, {
      bypassSigning: caller?.isFounder ?? false,
    });
    if (!signingCtx.signingValid) {
      json(res, 401, { error: 'signing-rejected', reason: signingCtx.signingReason });
      return true;
    }
    const body: any = effectiveBody;
    const observations = Array.isArray(body.observations)
      ? (body.observations as unknown[])
          .filter((x): x is string => typeof x === 'string')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
    if (observations.length === 0) {
      json(res, 400, { error: 'Missing observations[]' });
      return true;
    }

    const goal = (body.goal as string | undefined)?.trim();
    const existing = selfImprovingWorldStore.get(worldId);
    const patches = inferWorldPatches(observations, goal);
    const session: SelfImprovingWorldSession = {
      worldId,
      revision: existing ? existing.revision : 1,
      lastGoal: goal,
      patches,
      updatedAt: new Date().toISOString(),
    };

    selfImprovingWorldStore.set(worldId, session);
    persistSocialStore();
    json(res, 200, { success: true, session, generatedPatches: patches.length });
    return true;
  }

  // POST /api/holomesh/worlds/:id/self-improve/redeploy — apply selected patches and bump revision
  if (
    pathname.match(/^\/api\/holomesh\/worlds\/[^/]+\/self-improve\/redeploy$/) &&
    method === 'POST'
  ) {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const worldId = extractParam(url, '/api/holomesh/worlds/').replace(
      '/self-improve/redeploy',
      ''
    );
    const rawBody = await parseJsonBody(req);
    const { effectiveBody, ctx: signingCtx } = await extractAndVerifySigning(rawBody, {
      bypassSigning: caller?.isFounder ?? false,
    });
    if (!signingCtx.signingValid) {
      json(res, 401, { error: 'signing-rejected', reason: signingCtx.signingReason });
      return true;
    }
    const body: any = effectiveBody;
    const session = selfImprovingWorldStore.get(worldId);
    if (!session) {
      json(res, 404, { error: 'No self-improvement plan found for world' });
      return true;
    }

    const patchIds = Array.isArray(body.patchIds)
      ? (body.patchIds as unknown[]).filter((x): x is string => typeof x === 'string')
      : session.patches.map((p) => p.id);
    const applied = session.patches.filter((p) => patchIds.includes(p.id));

    const next: SelfImprovingWorldSession = {
      ...session,
      revision: session.revision + 1,
      patches: session.patches,
      updatedAt: new Date().toISOString(),
    };
    selfImprovingWorldStore.set(worldId, next);
    persistSocialStore();

    json(res, 200, {
      success: true,
      worldId,
      redeployedBy: caller.name,
      revision: next.revision,
      appliedPatches: applied,
      count: applied.length,
    });
    return true;
  }

  // GET /api/holomesh/worlds/:id/self-improve — inspect latest self-improvement session
  if (pathname.match(/^\/api\/holomesh\/worlds\/[^/]+\/self-improve$/) && method === 'GET') {
    const worldId = extractParam(url, '/api/holomesh/worlds/').replace('/self-improve', '');
    const session = selfImprovingWorldStore.get(worldId);
    if (!session) {
      json(res, 404, { error: 'No self-improvement session found' });
      return true;
    }
    json(res, 200, { success: true, session });
    return true;
  }

  // POST /api/holomesh/crosspost/moltbook — Crosspost a knowledge entry to Moltbook
  if (pathname === '/api/holomesh/crosspost/moltbook' && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const rawBody = await parseJsonBody(req);
    const { effectiveBody, ctx: signingCtx } = await extractAndVerifySigning(rawBody, {
      bypassSigning: caller?.isFounder ?? false,
    });
    if (!signingCtx.signingValid) {
      json(res, 401, { error: 'signing-rejected', reason: signingCtx.signingReason });
      return true;
    }
    const body: any = effectiveBody;
    const entryId = (body.entry_id as string | undefined)?.trim();
    if (!entryId) {
      json(res, 400, { error: 'Missing required field: entry_id' });
      return true;
    }

    const results = await getClient().queryKnowledge(entryId, { limit: 20 });
    const entry = results.find((r) => r.id === entryId);
    if (!entry) {
      json(res, 404, { error: 'Knowledge entry not found' });
      return true;
    }

    if (entry.authorId !== caller.id) {
      json(res, 403, { error: 'Only the entry author can crosspost' });
      return true;
    }

    // Phase-3 wrapped read: gated by `env:MOLTBOOK_API_KEY` lease when
    // HOLOMESH_VAULT_LEASE_ENFORCE is on; transparent passthrough otherwise.
    const moltbookKey = readMoltbookApiKey();
    if (!moltbookKey) {
      json(res, 503, { error: 'MOLTBOOK_API_KEY not configured — crosspost unavailable' });
      return true;
    }

    let built: { title: string; content: string; submolt: string };
    try {
      built = buildMoltbookCrosspostPayload({
        type: entry.type,
        id: entry.id,
        content: entry.content,
        domain: entry.domain ?? 'general',
        tags: entry.tags ?? [],
        title: (entry.metadata?.title as string) || undefined,
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      json(res, 400, { error: 'Could not build Moltbook payload', detail: msg });
      return true;
    }

    const posted = await createMoltbookPost({
      apiKey: moltbookKey,
      title: built.title,
      content: built.content,
      submolt: built.submolt,
    });

    if (!posted.success) {
      json(res, 502, {
        error: 'Moltbook API rejected the post',
        details: posted.details,
        entry_id: entryId,
      });
      return true;
    }

    json(res, 200, {
      success: true,
      crossposted: true,
      entry_id: entryId,
      platform: 'moltbook',
      moltbook: posted.data,
    });
    return true;
  }

  // ── Consolidation Bridge Routes ──

  // POST /api/holomesh/consolidation/trigger — manual consolidation cycle
  if (pathname === '/api/holomesh/consolidation/trigger' && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const rawBody = await parseJsonBody(req);
    const { effectiveBody, ctx: signingCtx } = await extractAndVerifySigning(rawBody, {
      bypassSigning: caller?.isFounder ?? false,
    });
    if (!signingCtx.signingValid) {
      json(res, 401, { error: 'signing-rejected', reason: signingCtx.signingReason });
      return true;
    }
    const body: any = effectiveBody;
    const reason = (body.reason as string | undefined) || `manual by ${caller.name}`;

    const bridge = getConsolidationBridge();
    const results = bridge.triggerManual(reason);

    json(res, 200, {
      success: true,
      trigger: 'manual',
      results,
      stats: bridge.getStats(),
    });
    return true;
  }

  // GET /api/holomesh/consolidation/review — review quarantined + rejected sets
  if (pathname === '/api/holomesh/consolidation/review' && method === 'GET') {
    const caller = resolveRequestingAgent(req);
    if (!caller.authenticated) {
      json(res, 401, { error: 'Authentication required' });
      return true;
    }

    const bridge = getConsolidationBridge();
    const review = bridge.getReviewSet();

    json(res, 200, {
      success: true,
      quarantined: review.quarantined.map((q) => ({
        id: q.entry.id,
        state: q.state,
        reasons: q.reasons,
        quarantinedAt: q.quarantinedAt,
        contentPreview: q.entry.content.slice(0, 200),
        domain: q.entry.domain,
      })),
      rejected: review.rejected.map((q) => ({
        id: q.entry.id,
        state: q.state,
        reasons: q.reasons,
        quarantinedAt: q.quarantinedAt,
        rejectedAt: q.rejectedAt,
        contentPreview: q.entry.content.slice(0, 200),
        domain: q.entry.domain,
      })),
      stats: review.stats,
    });
    return true;
  }

  // POST /api/holomesh/consolidation/review/:id/reject — reject a quarantined entry
  if (
    pathname.match(/^\/api\/holomesh\/consolidation\/review\/[^/]+\/reject$/) &&
    method === 'POST'
  ) {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const entryId = extractParam(url, '/api/holomesh/consolidation/review/').replace('/reject', '');
    const rawBody = await parseJsonBody(req);
    const { effectiveBody, ctx: signingCtx } = await extractAndVerifySigning(rawBody, {
      bypassSigning: caller?.isFounder ?? false,
    });
    if (!signingCtx.signingValid) {
      json(res, 401, { error: 'signing-rejected', reason: signingCtx.signingReason });
      return true;
    }
    const body: any = effectiveBody;
    const reason = (body.reason as string | undefined) || `rejected by ${caller.name}`;

    const bridge = getConsolidationBridge();
    // Try each domain — entryId is unique across domains
    let ok = false;
    let foundDomain = '';
    for (const domain of ['security', 'rendering', 'agents', 'compilation', 'general'] as const) {
      if (
        bridge
          .getReviewSet()
          .quarantined.some((q) => q.entry.id === entryId && q.entry.domain === domain)
      ) {
        ok = bridge.rejectQuarantined(domain, entryId, reason);
        if (ok) {
          foundDomain = domain;
          break;
        }
      }
    }

    if (!ok) {
      json(res, 404, { error: 'Quarantined entry not found' });
      return true;
    }

    json(res, 200, { success: true, entryId, domain: foundDomain, reason });
    return true;
  }

  // POST /api/holomesh/consolidation/rollback — rollback last N cycles
  if (pathname === '/api/holomesh/consolidation/rollback' && method === 'POST') {
    const caller = requireAuth(req, res);
    if (!caller) return true;

    const rawBody = await parseJsonBody(req);
    const { effectiveBody, ctx: signingCtx } = await extractAndVerifySigning(rawBody, {
      bypassSigning: caller?.isFounder ?? false,
    });
    if (!signingCtx.signingValid) {
      json(res, 401, { error: 'signing-rejected', reason: signingCtx.signingReason });
      return true;
    }
    const body: any = effectiveBody;
    const depth = Math.max(1, Math.min(10, parseInt(String(body.depth ?? 1), 10)));

    const bridge = getConsolidationBridge();
    const { discarded, domains } = bridge.rollback({ depth });

    json(res, 200, {
      success: true,
      depth,
      discarded,
      domains,
      stats: bridge.getStats(),
    });
    return true;
  }

  // GET /api/holomesh/consolidation/logs — run logs
  if (pathname === '/api/holomesh/consolidation/logs' && method === 'GET') {
    const caller = resolveRequestingAgent(req);
    if (!caller.authenticated) {
      json(res, 401, { error: 'Authentication required' });
      return true;
    }

    const q = parseQuery(url);
    const limit = Math.max(1, Math.min(100, parseInt(q.get('limit') || '20', 10)));
    const bridge = getConsolidationBridge();
    const logs = bridge.getRunLogs().slice(-limit);

    json(res, 200, {
      success: true,
      logs: logs.map((l) => ({
        id: l.id,
        startedAt: l.startedAt,
        finishedAt: l.finishedAt,
        trigger: l.trigger,
        rolledBack: l.rolledBack,
        rollbackAt: l.rollbackAt,
        reason: l.reason,
        results: l.results,
        promotedCount: l.promotedEntryIds.length,
        quarantinedCount: l.quarantinedEntryIds.length,
        rejectedCount: l.rejectedEntryIds.length,
      })),
      stats: bridge.getStats(),
    });
    return true;
  }

  // POST /api/holomesh/codebase/search — bearer-gated in-process GraphRAG semantic search
  // Used by edge agents (holoscript-agent rag_query cognitive verb, Phase 2.3 W.753) so they
  // can reach the real semantic stack (HoloEmbed) without a direct Absorb service dep or
  // a remote-auth bridge. The mesh server holds the in-process EmbeddingIndex (populated by
  // holo_absorb_repo); edge passes its mesh bearer, server calls isGraphRAGReady() and
  // handleGraphRagTool('holo_semantic_search', ...) in-process, returns ranked symbols.
  if (pathname === '/api/holomesh/codebase/search' && method === 'POST') {
    const caller = resolveRequestingAgent(req);
    if (!caller.authenticated) {
      json(res, 401, { error: 'Authentication required' });
      return true;
    }

    const body: any = await parseJsonBody(req);
    const query = typeof body.query === 'string' ? body.query.trim() : '';
    if (!query) {
      json(res, 400, { error: 'query is required (string)' });
      return true;
    }
    const topK = typeof body.topK === 'number' ? Math.max(1, Math.min(20, body.topK)) : 8;
    const language = typeof body.language === 'string' ? body.language : undefined;
    const type = typeof body.type === 'string' ? body.type : undefined;

    let graphReady = false;
    let handleGraphRagTool: ((name: string, args: Record<string, unknown>) => Promise<unknown>) | undefined;
    try {
      const absorbMcp = await import('@holoscript/absorb-service/mcp');
      graphReady = absorbMcp.isGraphRAGReady();
      handleGraphRagTool = absorbMcp.handleGraphRagTool;
    } catch {
      // absorb-service not available in this build
    }

    if (!graphReady || !handleGraphRagTool) {
      json(res, 503, {
        error: 'codebase-graph-not-ready',
        hint: 'The in-process GraphRAG index is not loaded. Run holo_absorb_repo on the prod checkout first, or use GET /team/:id/knowledge?q= for semantic knowledge-store search (already wired to HoloEmbed ranking).',
      });
      return true;
    }

    try {
      const args: Record<string, unknown> = { query, topK };
      if (language) args.language = language;
      if (type) args.type = type;
      const result = await handleGraphRagTool('holo_semantic_search', args);
      json(res, 200, { success: true, result });
    } catch (err: unknown) {
      json(res, 500, {
        error: 'semantic-search-failed',
        detail: err instanceof Error ? err.message : String(err),
      });
    }
    return true;
  }

  return false;
}
